<template>
  <div>
    <div ref="chartdiv" class="miniature-line-chart"></div>
  </div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import amThemeAnimated from '@amcharts/amcharts4/themes/animated'

import('@/styles/components/chart.less')
const DATA_WINDOW_STEP_SEC = 60

export default {
  name: 'TrainingMiniatureChart',
  components: {},
  props: {
    rhythmogram: {
      type: Object,
      required: true
    },
    minHR: {
      type: Number,
      required: false
    },
    maxHR: {
      type: Number,
      required: false
    },
    chartWindowDuration: {
      type: Number,
      required: true
    }
  },
  data: () => ({
    graphDuration: null,
    chartData: [],
    minHRValue: null,
    maxHRValue: null
  }),
  computed: {
    hrLength() {
      return this.rhythmogram.timeValues.length
    },
    chartDataLast() {
      return this.chartData[this.chartData.length - 1]
    }
  },
  watch: {
    rhythmogram: {
      deep: true,
      handler() {
        this.chart.addData({
          duration: this.rhythmogram.timeValues[this.hrLength - 1],
          hr: this.rhythmogram.hrValues[this.hrLength - 1],
          lineColor: this.getColorHR(this.rhythmogram.hrValues[this.hrLength - 1])
        })

        if (
          this.rhythmogram.timeValues[this.hrLength - 1] > this.chartWindowDuration &&
          this.chartData[0].duration < this.durationAxis.min
        ) {
          this.chart.removeData(1)
        }

        if (this.rhythmogram.hrValues[this.hrLength - 1] > this.maxHRValue) {
          this.maxHRValue = this.rhythmogram.hrValues[this.hrLength - 1]
          this.valueAxis.max = this.maxHRValue
        }
      }
    },
    chartData() {
      if (this.chartDataLast.duration >= this.durationAxis.max) {
        const newAxisMax = Math.ceil(this.chartDataLast.duration / DATA_WINDOW_STEP_SEC) * DATA_WINDOW_STEP_SEC
        const newAxisMin = newAxisMax - this.chartWindowDuration
        this.durationAxis.max = newAxisMax
        this.durationAxis.min = newAxisMin
        this.$emit('change:chartWindow', newAxisMin)
      }
    }
  },
  mounted() {
    this.minHRValue = this.minHR
    this.maxHRValue = this.maxHR
    am4core.useTheme(amThemeAnimated)
    this.createChart()
    this.setChartData()
    this.createGraph()
  },
  beforeDestroy() {    
    if (this.chart && !this.chart.isDisposed()) {
      this.chart.dispose()
    }
  },
  methods: {
    createChart() {
      this.chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart)
      this.chart.paddingLeft = 0
      this.chart.colors.list = [am4core.color('#1A1A1B'), am4core.color('#FAAD14'), am4core.color('#F5222D')]
    },
    setChartData() {
      let data = [
        {
          duration: 0,
          hr: Math.round(this.rhythmogram.hrValues[0]),
          lineColor: this.getColorHR(this.rhythmogram.hrValues[0])
        }
      ]

      let startIndex = 0
      const lastTimeValue = this.rhythmogram.timeValues.at(-1)

      // #75194 Теперь нужно отображать только новые данные, попадающее во временное окно
      if (lastTimeValue > this.chartWindowDuration) {
        startIndex = this.rhythmogram.timeValues.findIndex(
          item => item >= lastTimeValue - this.chartWindowDuration
        )
      }

      for (let i = startIndex; i < this.hrLength; i++) {
        let time = Math.round(this.rhythmogram.timeValues[i])
        let dataItem = {
          duration: time,
          hr: Math.round(this.rhythmogram.hrValues[i]),
          lineColor: this.getColorHR(this.rhythmogram.hrValues[i])
        }
        if (dataItem.hr > this.maxHRValue) {
          this.maxHRValue = dataItem.hr
        }

        data.push(dataItem)
      }

      this.chartData = data
    },
    createGraph() {
      this.chart.data = this.chartData
      this.chart.logo.disabled = true

      //x
      this.durationAxis = this.chart.xAxes.push(new am4charts.DurationAxis())
      this.durationAxis.renderer.opposite = true
      this.durationAxis.renderer.grid.template.disabled = true
      this.durationAxis.renderer.labels.template.inside = true
      this.durationAxis.renderer.labels.template.dy = -22
      this.durationAxis.renderer.labels.template.fontSize = 12
      this.durationAxis.renderer.labels.template.fill = am4core.color('#67686A')
      this.durationAxis.min = 0
      this.durationAxis.max = this.chartWindowDuration
      this.durationAxis.baseUnit = 'second'
      this.durationAxis.strictMinMax = true

      this.durationAxis.renderer.labels.template.adapter.add('text', function(text) {
        if (text) {
          if (text.includes('0d')) {
            text = text.replace('0d ', '')
          }
          return text === '00:00' || text === '00:00:00' ? '' : text
        }
      })

      //y
      this.valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis())
      this.valueAxis.renderer.grid.template.disabled = true
      this.valueAxis.renderer.labels.template.disabled = true
      this.valueAxis.max = this.maxHRValue
      this.valueAxis.min = 0

      this.hrSeries = this.chart.series.push(new am4charts.LineSeries())
      this.hrSeries.dataFields.valueY = 'hr'
      this.hrSeries.dataFields.valueX = 'duration'
      this.hrSeries.id = 'hrSeries'
      this.hrSeries.name = this.$t('sessionsInfo.heartRate')
      this.hrSeries.numberFormatter.numberFormat = '#.'
      this.hrSeries.strokeWidth = 2
      this.hrSeries.propertyFields.fill = 'lineColor'

      this.hrSeries.fill = am4core.color('#1A1A1B')
      this.hrSeries.fillOpacity = 0.045

      /* Метка макс. ЧСС сейчас очень неудачно располагается на графике, пока скроем
      const label = this.chart.plotContainer.createChild(am4core.Label)
      label.text = this.maxHRValue
      label.fontSize = 12
      label.align = 'left'
      label.valign = 'top'
      label.padding(-12, 0, 0, 5)
      label.fill = am4core.color('#67686A')
      */

      if (this.maxHR) {
        this.createYRanges(this.maxHR, this.maxHR)
        this.createSeriesRange(this.maxHR, 1000, 2)
      }
      if (this.minHR) {
        this.createYRanges(this.minHR, this.minHR)
        this.createSeriesRange(this.minHR, 0, 1)
      }
    },
    createYRanges(value, name, fill = '#67686A') {
      let axisRange = this.valueAxis.axisRanges.create()
      axisRange.value = value
      axisRange.grid.stroke = fill
      axisRange.grid.strokeWidth = 1.5
      axisRange.grid.strokeOpacity = 0.5
      axisRange.grid.strokeDasharray = '1, 5'

      axisRange.label.text = name
      axisRange.label.background.fill = '#fff'
      axisRange.label.background.fillOpacity = 0.7

      axisRange.label.inside = true
      axisRange.label.align = 'left'
      axisRange.label.verticalCenter = 'bottom'
      axisRange.label.fill = fill
      axisRange.label.fontSize = '12px'
      axisRange.label.dx = 3
      axisRange.label.dy = 0
      axisRange.label.padding(0, 0, -2, 4)
    },
    createSeriesRange(value, endValue, colorIndex) {
      let range = this.valueAxis.createSeriesRange(this.hrSeries)
      range.value = value
      range.endValue = endValue
      range.contents.stroke = this.chart.colors.getIndex(colorIndex)
      range.contents.fill = range.contents.strokeDasharray
      range.contents.fillOpacity = 0.045
    },
    getColorHR(value) {
      let color
      if (!this.maxHR && !this.minHR) {
        color = this.chart.colors.getIndex(0)
      } else if (Math.round(value) < this.minHR) {
        color = this.chart.colors.getIndex(1)
      } else if (Math.round(value) > this.maxHR) {
        color = this.chart.colors.getIndex(2)
      } else {
        color = this.chart.colors.getIndex(0)
      }
      return color
    }
  }
}
</script>
